import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { Text } from 'designSystem/components/Text'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { extractAndDownloadPages } from 'features/knowledgeDocumentViewer/utils/downloadDocumentPages'
import { Download } from 'lucide-react'
import { useState } from 'react'
import styled from 'styled-components'
import { downloadFromUrl } from 'utils/download'

const PageRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
`

const PageRangeInput = styled.input`
  width: 3.5rem;
  padding: 0.25rem 0.375rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  background: transparent;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.primary};
  }
`

const RangeDownloadButton = styled.button`
  padding: 0.25rem 0.625rem;
  align-self: stretch;
  border: none;
  border-radius: 0.25rem;
  background: ${({ theme }) => theme.forecast.primary};
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const ValidationMessage = styled.div`
  padding: 0 0.5rem 0.25rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.danger};
`

function isPdfDocument(documentType: string | undefined): boolean {
  return documentType?.toLowerCase() === 'pdf'
}

export function DownloadDocumentButton() {
  const { documentSignedURL, documentFilename, documentType, totalPages } =
    useDocumentTabDataContext()
  const { currentPageNumber } = useKnowledgeDocumentUXContext()

  const [isDownloading, setIsDownloading] = useState(false)
  const [rangeStart, setRangeStart] = useState('')
  const [rangeEnd, setRangeEnd] = useState('')

  const signedUrl = documentSignedURL?.signed_url
  const isDisabled = !signedUrl || !documentFilename

  const showDropdown = totalPages > 1 && isPdfDocument(documentType)

  const handleFullDownload = async () => {
    if (!signedUrl || !documentFilename) return
    setIsDownloading(true)
    try {
      await downloadFromUrl(signedUrl, documentFilename)
    } catch (error) {
      console.error('Failed to download document:', error)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleCurrentPageDownload = async () => {
    if (!signedUrl || !documentFilename) return
    setIsDownloading(true)
    try {
      await extractAndDownloadPages(
        signedUrl,
        documentFilename,
        currentPageNumber,
        currentPageNumber
      )
    } catch (error) {
      console.error('Failed to download current page:', error)
    } finally {
      setIsDownloading(false)
    }
  }

  const parsedStart = parseInt(rangeStart, 10)
  const parsedEnd = parseInt(rangeEnd, 10)
  const isRangeValid =
    !isNaN(parsedStart) &&
    !isNaN(parsedEnd) &&
    parsedStart >= 1 &&
    parsedEnd <= totalPages &&
    parsedStart <= parsedEnd

  const rangeValidationMessage =
    rangeStart && rangeEnd && !isRangeValid
      ? `Enter pages between 1 and ${totalPages}`
      : undefined

  const handleRangeDownload = async () => {
    if (!signedUrl || !documentFilename || !isRangeValid) return
    setIsDownloading(true)
    try {
      await extractAndDownloadPages(
        signedUrl,
        documentFilename,
        parsedStart,
        parsedEnd
      )
    } catch (error) {
      console.error('Failed to download page range:', error)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleRangeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isRangeValid) {
      handleRangeDownload()
    }
    e.stopPropagation()
  }

  if (!showDropdown) {
    return (
      <Button
        variant='inputRaised'
        size='icon-lg'
        title='Download Document'
        onClick={handleFullDownload}
        disabled={isDisabled || isDownloading}>
        <Download size={18} strokeWidth={1.5} />
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='raised'
          size='icon-lg'
          title='Download Document'
          disabled={isDisabled || isDownloading}>
          <Download size={18} strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem onSelect={handleCurrentPageDownload}>
          Current page ({currentPageNumber})
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuLabel>Page range</DropdownMenuLabel>
        <PageRangeContainer>
          <PageRangeInput
            type='number'
            min={1}
            max={totalPages}
            placeholder='From'
            value={rangeStart}
            onChange={(e) => setRangeStart(e.target.value)}
            onKeyDown={handleRangeKeyDown}
          />
          <Text as='span' size='sm' color='muted'>
            to
          </Text>
          <PageRangeInput
            type='number'
            min={1}
            max={totalPages}
            placeholder='To'
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
            onKeyDown={handleRangeKeyDown}
          />
          <RangeDownloadButton
            onClick={handleRangeDownload}
            disabled={!isRangeValid || isDownloading}>
            <Download size={14} strokeWidth={2} />
          </RangeDownloadButton>
        </PageRangeContainer>
        {rangeValidationMessage && (
          <ValidationMessage>{rangeValidationMessage}</ValidationMessage>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={handleFullDownload}>
          Entire document ({totalPages} pages)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
